"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { aiMasteringSessionStorage } from "@/services/storage/ai-mastering-session-storage";
import {
  masterPreset,
  masterReference,
} from "@/components/pages/ai-mastering/providers/mastering";
import { useAnonServiceChecker } from "@/hooks/use-anon-service-checker";
import { aiMasteringLicenseManager } from "@/services/license/license-managers/ai-mastering-license-manager";
import { useMasteringProgressContext } from "@/components/pages/ai-mastering/hooks/mastering-progress-provider";
import { AiMasteringResponse } from "@/app/api/ai-mastering/route";

type OptionTab = "preset" | "referenceTrack";

type AiMasteringContextType = {
  targetTrack?: File;
  setTargetTrack: (track?: File) => void;
  referenceTrack?: File;
  setReferenceTrack: (track?: File) => void;
  selectedPreset?: string;
  setSelectedPreset: (preset?: string) => void;
  currentOptionTab: OptionTab;
  setCurrentOptionTab: (tab: OptionTab) => void;
  masteredTrack?: File;
  isValid: boolean;
  reset: () => void;
  master: () => Promise<void>;
  isLoading: boolean;
  clearMasteredTrack: () => void;
};

const AiMasteringContext = createContext<AiMasteringContextType>({
  currentOptionTab: "preset",
  setCurrentOptionTab: () => {},
  setTargetTrack: () => {},
  setReferenceTrack: () => {},
  setSelectedPreset: () => {},
  isValid: false,
  reset: () => {},
  master: async () => {},
  isLoading: false,
  clearMasteredTrack: () => {},
});

export const useAiMastering = () => useContext(AiMasteringContext);

export const AiMasteringProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentOptionTab, setCurrentOptionTab] = useState<OptionTab>("preset");
  const [targetTrack, setTargetTrack] = useState<File | undefined>(undefined);
  const [referenceTrack, setReferenceTrack] = useState<File | undefined>(
    undefined,
  );
  const [selectedPreset, setSelectedPreset] = useState<string | undefined>(
    undefined,
  );
  const [isValid, setIsValid] = useState<boolean>(false);

  const {
    masteredTrack,
    clearMasteredTrack,
    resetProgressContext,
    setProcessId,
    isLoading,
    ...progressContext
  } = useMasteringProgressContext();

  const { validate } = useAnonServiceChecker({
    serviceStorage: aiMasteringSessionStorage.aiMasteringStorage,
    authTrialUserUsageCount: 4,
    anonUserUsageCount: 2,
    serviceName: "AI Mastering",
    licenseValidator:
      aiMasteringLicenseManager.downloadResultLimiter.isDownloadEnabled,
  });

  const checkValid = () => {
    if (!targetTrack) {
      setIsValid(false);
      return;
    }

    if (currentOptionTab === "preset") {
      setIsValid(!!selectedPreset);
      return;
    }

    if (currentOptionTab === "referenceTrack") {
      setIsValid(!!referenceTrack);
      return;
    }
  };

  useEffect(() => {
    checkValid();
  }, [currentOptionTab, targetTrack, selectedPreset, referenceTrack]);

  const reset = async () => {
    setTargetTrack(undefined);
    setSelectedPreset(undefined);
    setReferenceTrack(undefined);
    await resetProgressContext();
  };

  const master = async () => {
    if (!(await validate())) {
      return;
    }

    if (!targetTrack) {
      console.error(
        "Failed to master track. Target track is undefined or null.",
      );
      return;
    }

    let masteringResponse: AiMasteringResponse | undefined = undefined;

    if (selectedPreset && currentOptionTab === "preset") {
      masteringResponse = await masterPreset(targetTrack, selectedPreset!);
    }

    if (referenceTrack && currentOptionTab === "referenceTrack") {
      masteringResponse = await masterReference(targetTrack, referenceTrack);
    }

    if (masteringResponse) {
      setProcessId(masteringResponse.masteringId);
    }
  };

  return (
    <AiMasteringContext.Provider
      value={{
        targetTrack,
        referenceTrack,
        currentOptionTab,
        selectedPreset,
        setTargetTrack,
        setReferenceTrack,
        setCurrentOptionTab,
        setSelectedPreset,
        isValid,
        reset,
        master,
        isLoading,
        masteredTrack,
        clearMasteredTrack,
      }}
    >
      {children}
    </AiMasteringContext.Provider>
  );
};
